import React, { useEffect, useRef, useState } from "react";
import { FaRegClipboard, FaPlane, FaClock, FaSuitcase } from "react-icons/fa";
import {
  getUserProfile,
  requestPaymentOtp,
  getDiscountCap,
  PaymentOtpChallenge,
  DiscountQuote,
} from "../../services/api";
import CashOtpDialog from "./CashOtpDialog";
import { useAuth } from "../../context/AuthContext";
import { isStaff } from "../../utils/roles";
import DailyLimitBar from "../common/DailyLimitBar";
import { useBooking, TripType } from "../../context/BookingContext";
import {
  getCabinLabel,
  formatDuration,
  formatTime,
} from "../../utils/passengerUtils";
import FareRulesDisplay from "./FareRulesDisplay";
import { SeatSelection } from "./SeatMap";
import { serviceFeeOf } from "../../utils/pricing";

/**
 * Put the dial code back on the number.
 *
 * The passenger form collects the two separately — a country dropdown and the
 * digits — and everything downstream was being given the digits alone. The
 * server then assumes UAE for any bare number, which is right for a walk-in
 * customer in Abu Dhabi and silently wrong for everyone else: an Indian
 * 8281177057 became +9718281177057, a real-looking number belonging to somebody
 * else entirely. WhatsApp accepted it and returned an id, so nothing looked
 * broken from this end and the ticket simply never arrived.
 *
 * Defaults to +971 to match what the form shows when the dropdown is untouched,
 * so the number that is sent is the number the person saw on screen.
 */
function withDialCode(countryCode: string | undefined, phone: string): string {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  // Already carries a country code — trust it and change nothing.
  if ((phone || "").trim().startsWith("+")) return (phone || "").trim();
  const dial = (countryCode || "+971").trim();
  return `${dial.startsWith("+") ? dial : `+${dial}`}${digits}`;
}

interface PassengerData {
  title: string;
  firstName: string;
  lastName: string;
  /** Dial code from the form's country dropdown, e.g. "+91". */
  countryCode?: string;
  phone: string;
  email: string;
  ffNumber?: string;
  passportNumber: string;
  dob: string;
  expiryDate: string;
  issuingCountry: string;
  agreed: boolean;
  insured: boolean;
  passengerType: "ADT" | "CHD" | "INF";
}


interface BookingDetails {
  flightData: any;
  passengerCounts: {
    ADT: number;
    CHD: number;
    INF: number;
  };
  departureDate: string;
  returnDate?: string;
  /** For display/convenience; segment dates for multicity (one per segment). */
  tripType?: TripType;
  multicitySegmentDates?: string[];
  classOfService: string;
  selectedClass: string;
  totalAmount: number;
}

interface Props {
  passengerData: PassengerData[];
  bookingDetails: BookingDetails;
  seatSelections?: SeatSelection[];
  onClose: () => void;
}

const ReviewAndPayPopup: React.FC<Props> = ({
  passengerData,
  bookingDetails,
  seatSelections = [],
  onClose,
}) => {
  // Transform client-side SeatSelection (segmentIndex, passengerIndex) to
  // server-compatible format (segmentNumber, nameNumber, segmentId).
  const serverSeatSelections = seatSelections.map((s) => ({
    segmentIndex: s.segmentIndex,
    passengerIndex: s.passengerIndex,
    rowNumber: s.rowNumber,
    seatNumber: s.seatNumber,
    offerItemId: s.offerItemId,
    amount: s.amount,
    currency: s.currency,
    segmentNumber: String(s.segmentIndex + 1),
    segmentId: String(s.segmentIndex + 1),
    nameNumber: `${s.passengerIndex + 1}.1`,
  }));

  const modalRef = useRef<HTMLDivElement>(null);
  /**
   * The dimmed area behind this popup.
   *
   * Dismissal is decided by what was actually clicked rather than by "anything
   * that is not inside the panel". The OTP dialog is a *sibling* of the panel,
   * so under the old test every click inside it — the code box included —
   * counted as outside and tore the whole popup down.
   */
  const backdropRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const { user, profile } = useAuth();
  const { sessionId, revalId, setBookingId } = useBooking();
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<{
    customId?: string;
    role?: string;
  }>({});

  // Online card payment is switched off for phase 1: staff take cash, and card
  // is handled on the branch POS machine outside this system. Mirrors
  // CARD_PAYMENTS_ENABLED on the server, which is what actually refuses it —
  // this only decides what the screen offers.
  const cardEnabled = process.env.REACT_APP_CARD_PAYMENTS_ENABLED === "true";

  const role = profile?.role ?? userProfile?.role;
  const canChoosePayment =
    isStaff(profile) || isStaff(userProfile as any);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "cash">("cash");
  useEffect(() => {
    // With card switched off there is only one way to pay, whoever you are.
    if (!cardEnabled) return setPaymentMethod("cash");
    setPaymentMethod(canChoosePayment ? "cash" : "card");
  }, [canChoosePayment, cardEnabled]);

  // The code the agent has to enter before a cash sale completes. Held here
  // rather than inside the dialog because the booking already exists by the
  // time it appears — closing the dialog leaves that booking unpaid, it does
  // not undo it.
  const [cashOtp, setCashOtp] = useState<{
    bookingId: string;
    challenge: PaymentOtpChallenge;
  } | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.uid) {
        try {
          const data = await getUserProfile(user.uid);
          setUserProfile(data);
        } catch (err) {
          console.error("[ReviewAndPayPopup] Failed to fetch user profile:", err);
        }
      }
    };
    fetchProfile();
  }, [user]);
  // Escape and click-away are both switched off while the cash OTP dialog is
  // up. By that point the booking exists and the customer is at the counter:
  // the only two ways on are approving the payment or deliberately abandoning
  // it, and both are buttons in that dialog. Dismissing the popup by accident
  // leaves a real booking unpaid with nothing on screen to say so.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !cashOtp) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, cashOtp]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (cashOtp) return;
      // Close only when the dimmed backdrop itself was clicked. The old test —
      // "not contained by the panel" — is true of every element rendered
      // outside it, which includes the whole OTP dialog.
      if (e.target === backdropRef.current) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onClose, cashOtp]);
  // ── Taking an amount off the fare ────────────────────────────────────────
  // The ceiling comes from the server, keyed on the revalidation about to be
  // confirmed. It is deliberately not worked out here from `flightData`: the
  // discount feature that used to live on these screens did exactly that, read
  // the wrong fare's service fee once a tier had been chosen, and offered
  // agents discounts the server then refused.
  const [discountCap, setDiscountCap] = useState<DiscountQuote | null>(null);
  /** Strings, so the boxes can be empty mid-type rather than snapping to 0. */
  const [discountInput, setDiscountInput] = useState("");
  const [extraFeeInput, setExtraFeeInput] = useState("");

  useEffect(() => {
    // Re-fetched whenever the quote changes, so a cap can never outlive the
    // fare it was calculated for.
    let live = true;
    setDiscountCap(null);
    setDiscountInput("");
    setExtraFeeInput("");
    if (!revalId) return;
    getDiscountCap(revalId)
      .then((quote) => { if (live) setDiscountCap(quote); })
      .catch(() => { if (live) setDiscountCap(null); });
    return () => { live = false; };
  }, [revalId]);

  const amount = bookingDetails.flightData?.pricingInfo?.total ?? bookingDetails.flightData?.totalFare?.amount ?? 0;
  const baseFare = bookingDetails.flightData?.pricingInfo?.baseFare ?? bookingDetails.flightData?.totalFare?.baseFare ?? 0;
  const taxes = bookingDetails.flightData?.pricingInfo?.taxes ?? bookingDetails.flightData?.totalFare?.taxes ?? 0;
  const currency = bookingDetails.flightData?.pricingInfo?.currency ?? bookingDetails.flightData?.totalFare?.currency ?? "AED";
  const serviceFee = serviceFeeOf(bookingDetails.flightData);
  // Chargeable seats are billed on top of the flight fare. `bookingDetails.totalAmount`
  // already includes them (that's what we charge); surface the breakdown here so the
  // displayed Total matches the amount sent to /create-order.
  const seatTotal = seatSelections.reduce((sum, s) => sum + (s.amount || 0), 0);
  const quotedTotal = Number(bookingDetails.totalAmount ?? amount);

  /**
   * What is actually coming off, and what is wrong with what was typed.
   *
   * Both derived from the input on every render rather than held in state, so
   * there is no second copy of the discount to go stale against the fare — the
   * failure mode that produced three findings against the previous attempt.
   * The server clamps against the same ceiling regardless; this exists so the
   * agent is stopped before they promise a customer something.
   */
  const maxDiscount = discountCap?.allowed ? discountCap.maxDiscount : 0;
  const typedDiscount = Number(discountInput);
  const discountValid =
    discountInput.trim() === "" ||
    (Number.isFinite(typedDiscount) && typedDiscount >= 0 && typedDiscount <= maxDiscount);
  const appliedDiscount =
    discountValid && Number.isFinite(typedDiscount) && typedDiscount > 0 ? typedDiscount : 0;
  // Only the amount can block the sale. The reason is optional on the server
  // too — an agent with a customer at the counter should not be stopped by a
  // note — so there is deliberately no check on it here.
  // The other direction. Uncapped on purpose — a discount spends the agency's
  // own margin and can reach zero, a surcharge takes more from the customer and
  // is a commercial call the agent is trusted with.
  const typedExtraFee = Number(extraFeeInput);
  const extraFeeValid =
    extraFeeInput.trim() === "" ||
    (Number.isFinite(typedExtraFee) && typedExtraFee >= 0);
  const appliedExtraFee =
    extraFeeValid && Number.isFinite(typedExtraFee) && typedExtraFee > 0 ? typedExtraFee : 0;

  // One or the other, never both. The server refuses the combination outright;
  // this only keeps the screen from offering it, by disabling whichever field
  // is not in use.
  const discountInUse = discountInput.trim() !== "";
  const extraFeeInUse = extraFeeInput.trim() !== "";

  const discountBlocks = !discountValid || !extraFeeValid;

  const grandTotal = Math.max(
    0,
    Math.round((quotedTotal - appliedDiscount + appliedExtraFee) * 100) / 100,
  );

  // Helper to build flight segments — supports both new legs[] and old onwardFlight/returnFlight
  const buildFlightSegments = () => {
    const fd = bookingDetails.flightData;
    const legs: any[] = fd?.legs ?? [];
    // New format: iterate legs → segments
    if (legs.length > 0 && legs[0]?.segments) {
      const segments: any[] = [];
      legs.forEach((leg: any) => {
        (leg.segments ?? []).forEach((seg: any) => {
          segments.push({
            arrivalDateTime: seg.arrival?.dateTime ?? "",
            departureDateTime: seg.departure?.dateTime ?? "",
            flightNumber: String(seg.marketingCarrier?.flightNumber ?? ""),
            numberInParty:
              bookingDetails.passengerCounts.ADT +
              bookingDetails.passengerCounts.CHD +
              bookingDetails.passengerCounts.INF,
            resBookDesigCode: seg.bookingInfo?.bookingCode ?? "",
            status: "NN",
            departureAirportCode: seg.departure?.airport?.code ?? "",
            arrivalAirportCode: seg.arrival?.airport?.code ?? "",
            airlineMarketingName: seg.marketingCarrier?.code ?? "",
            airlineMarketingNumber: String(seg.marketingCarrier?.flightNumber ?? ""),
            operatingCarrier: seg.operatingCarrier?.code ?? "",
            equipment: seg.equipment ?? "",
            fareBasisCode: seg.bookingInfo?.fareBasisCode ?? "",
          });
        });
      });
      return segments;
    }
    // Old format fallback: onwardFlight.schedules + returnFlight.schedules
    const mapSchedule = (s: any, baseDate: string) => {
      const adj = s.departureDateAdjustment || 0;
      const segmentDate = addDays(baseDate, adj);
      return {
        arrivalDateTime: formatSabreDateTime(segmentDate, s.arrival?.time),
        departureDateTime: formatSabreDateTime(segmentDate, s.departure?.time),
        flightNumber: String(s.flightNumber),
        numberInParty:
          bookingDetails.passengerCounts.ADT +
          bookingDetails.passengerCounts.CHD +
          bookingDetails.passengerCounts.INF,
        resBookDesigCode: s.bookingCode,
        status: "NN",
        departureAirportCode: s.departure?.airport,
        arrivalAirportCode: s.arrival?.airport,
        airlineMarketingName: s.flightCode,
        airlineMarketingNumber: String(s.flightNumber),
        operatingCarrier: s.operatingCarrier,
        equipment: s.equipment,
        fareBasisCode: s.fareBasisCode,
      };
    };
    const baseDep = typeof bookingDetails.departureDate === "string" ? bookingDetails.departureDate : "";
    const baseRet = typeof bookingDetails.returnDate === "string" ? bookingDetails.returnDate : "";
    return [
      ...(fd?.onwardFlight?.schedules || []).map((s: any) => mapSchedule(s, baseDep)),
      ...(baseRet ? (fd?.returnFlight?.schedules || []).map((s: any) => mapSchedule(s, baseRet)) : []),
    ];
  };

  // Card payment flow (existing)
  const handleCardPayment = async () => {
    if (!user) {
      alert("You must be logged in to proceed.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (!sessionId || !revalId) {
        setError(
          "Session expired or missing. Please search and select your flight again."
        );
        setLoading(false);
        return;
      }
      const confirmRes = await fetch(
        `${process.env.REACT_APP_API_URL}/confirm-selection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            sessionId,
            revalId,
            passengerData,
            bookingDetails,
            userId: user.uid,
            // Identities only — the server prices these from the quote Sabre
            // gave when the seat map was fetched. Any `amount` here is ignored,
            // which is the point: the seat is the customer's choice, the price
            // is not the browser's to state.
            seatSelections: serverSeatSelections,
            // Likewise a request, not an instruction: the server caps this at
            // the service fee it priced for this exact revalidation and refuses
            // anything above, rather than quietly trimming it.
            discountAmount: appliedDiscount > 0 ? appliedDiscount : undefined,
            additionalFee: appliedExtraFee > 0 ? appliedExtraFee : undefined,
          }),
        }
      );

      const confirmData = await confirmRes.json();
      if (!confirmRes.ok || !confirmData.bookingId) {
        const msg = confirmData?.errors?.[0]?.message ?? confirmData?.error ?? "Failed to confirm booking. Please try again.";
        setError(msg);
        setLoading(false);
        return;
      }
      setBookingId(confirmData.bookingId);

      const customerEmail = user.email;

      // Build flight segments using helper
      const flightSegments = buildFlightSegments();

      const passengerNames = passengerData.map((p, idx) => ({
        nameNumber: `${idx + 1}.1`,
        passengerType: p.passengerType,
        // Sabre's name is SURNAME/GIVENNAME TITLE — the title is a suffix, and
        // it is the passenger's own, not "MR" for everyone including women.
        // Inert today: /flights/pre-booking stores only passengerData, and the
        // server rebuilds the names from that. Corrected anyway so it is not a
        // trap for whoever wires this field through.
        givenName: [p.firstName, (p.title || "").trim().toUpperCase()].filter(Boolean).join(" "),
        surname: p.lastName,
        dob: p.dob,
        passportNumber: p.passportNumber,
        expiryDate: p.expiryDate,
        issuingCountry: p.issuingCountry,
      }));
      const passengerContacts = passengerData.map((p, idx) => ({
        nameNumber: `${idx + 1}.1`,
        phone: withDialCode(p.countryCode, p.phone),
        phoneUseType: "H",
      }));

      const preBookingRes = await fetch(
        `${process.env.REACT_APP_API_URL}/flights/pre-booking`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            bookingId: confirmData.bookingId,
            userId: user.uid,
            passengerData,
            bookingDetails,
            flightSegments,
            passengerNames,
            revalId,
            passengerContacts,
            customerEmail,
            seatSelections: serverSeatSelections,
          }),
        }
      );
      if (!preBookingRes.ok) {
        setError("Failed to initiate booking. Please try again.");
        setLoading(false);
        return;
      }

      // 3. Initiate payment
      const paymentRes = await fetch(
        `${process.env.REACT_APP_API_URL}/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            amount: grandTotal,
            currency: "AED",
            bookingId: confirmData.bookingId,
            customerEmail,
            userId: user.uid,
            reference: confirmData.bookingId,
          }),
        }
      );

      const paymentData = await paymentRes.json();
      if (paymentData.paymentUrl) {
        window.location.href = paymentData.paymentUrl;
      } else {
        setError("Payment initiation failed. Please try again.");
      }
    } catch (err) {
      setError("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Cash payment flow (uses new layered architecture service)
  const handleCashPayment = async () => {
    if (!user) {
      alert("You must be logged in to proceed.");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      if (!sessionId || !revalId) {
        setError(
          "Session expired or missing. Please search and select your flight again."
        );
        setLoading(false);
        return;
      }
      // STEP 1: Create booking
      const confirmRes = await fetch(
        `${process.env.REACT_APP_API_URL}/confirm-selection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            sessionId,
            revalId,
            passengerData,
            bookingDetails,
            userId: user.uid,
            paymentMethod: "cash",
            // Identities only — the server prices these from the quote Sabre
            // gave when the seat map was fetched. Any `amount` here is ignored,
            // which is the point: the seat is the customer's choice, the price
            // is not the browser's to state.
            seatSelections: serverSeatSelections,
            // Likewise a request, not an instruction: the server caps this at
            // the service fee it priced for this exact revalidation and refuses
            // anything above, rather than quietly trimming it.
            discountAmount: appliedDiscount > 0 ? appliedDiscount : undefined,
            additionalFee: appliedExtraFee > 0 ? appliedExtraFee : undefined,
          }),
        }
      );

      const confirmData = await confirmRes.json();
      if (!confirmRes.ok || !confirmData.bookingId) {
        const msg = confirmData?.errors?.[0]?.message ?? confirmData?.error ?? "Failed to confirm booking. Please try again.";
        setError(msg);
        setLoading(false);
        return;
      }

      setBookingId(confirmData.bookingId);

      const customerEmail = user.email;

      // Build flight segments using helper
      const flightSegments = buildFlightSegments();

      const passengerNames = passengerData.map((p, idx) => ({
        nameNumber: `${idx + 1}.1`,
        passengerType: p.passengerType,
        // Sabre's name is SURNAME/GIVENNAME TITLE — the title is a suffix, and
        // it is the passenger's own, not "MR" for everyone including women.
        // Inert today: /flights/pre-booking stores only passengerData, and the
        // server rebuilds the names from that. Corrected anyway so it is not a
        // trap for whoever wires this field through.
        givenName: [p.firstName, (p.title || "").trim().toUpperCase()].filter(Boolean).join(" "),
        surname: p.lastName,
        dob: p.dob,
        passportNumber: p.passportNumber,
        expiryDate: p.expiryDate,
        issuingCountry: p.issuingCountry,
      }));

      const passengerContacts = passengerData.map((p, idx) => ({
        nameNumber: `${idx + 1}.1`,
        phone: withDialCode(p.countryCode, p.phone),
        phoneUseType: "H",
      }));

      // STEP 2: Save pre-booking data (required for ticketing service)
      const preBookingRes = await fetch(
        `${process.env.REACT_APP_API_URL}/flights/pre-booking`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            bookingId: confirmData.bookingId,
            userId: user.uid,
            passengerData,
            bookingDetails,
            flightSegments,
            passengerNames,
            revalId,
            passengerContacts,
            customerEmail,
            seatSelections: serverSeatSelections,
          }),
        }
      );

      if (!preBookingRes.ok) {
        setError("Failed to initiate booking. Please try again.");
        setLoading(false);
        return;
      }

      // STEP 3: Approve the sale with the code sent to the agent's phone.
      // The server refuses to issue a ticket for a sale nobody has approved, so
      // this is not a formality that can be skipped by going straight on.
      const challenge = await requestPaymentOtp(confirmData.bookingId);
      if (challenge.otpRequired) {
        setCashOtp({ bookingId: confirmData.bookingId, challenge });
        setLoading(false);
        return;
      }

      // STEP 4: Issue the ticket
      await completeTicketing(confirmData.bookingId);
    } catch (err: any) {
      console.error("Cash payment error:", err);
      // The daily limit and the approval rules both refuse with something the
      // agent can act on. Losing that behind a generic message would leave them
      // retrying a sale that will never go through.
      setError(
        err?.response?.data?.errors?.[0]?.message ??
        "Failed to create booking. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /** Create the PNR and issue the ticket. Only ever called for an approved sale. */
  const completeTicketing = async (bookingIdToTicket: string) => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const ticketingRes = await fetch(
        `${process.env.REACT_APP_API_URL}/flights/process-cash-ticketing`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            bookingId: bookingIdToTicket,
            userId: user.uid,
          }),
        }
      );

      const ticketingData = await ticketingRes.json();

      if (!ticketingRes.ok || !ticketingData.success) {
        setError(
          ticketingData?.errors?.[0]?.message ||
          ticketingData.error ||
          "Failed to process booking. Please contact support."
        );
        setLoading(false);
        return;
      }

      window.location.href = `/booking-confirmed?pnr=${ticketingData.pnr}&bookingId=${bookingIdToTicket}`;
    } catch (err) {
      console.error("Ticketing error:", err);
      setError("Failed to issue the ticket. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | number | boolean | undefined;
  }) => (
    <div className="flex justify-between py-0.5">
      <span className="font-medium text-heading">{label}</span>
      <span className="text-right text-black">{String(value)}</span>
    </div>
  );

  // Get the actual displayed class from legs (new) or onwardFlight.schedules (old)
  const getDisplayedClass = () => {
    const legs: any[] = bookingDetails.flightData?.legs ?? [];
    const outboundLeg = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
    const cabinCode =
      outboundLeg?.segments?.[0]?.bookingInfo?.cabinCode ??
      bookingDetails.flightData?.onwardFlight?.schedules?.[0]?.cabinCode;
    return getCabinLabel(cabinCode);
  };

  // Helper function to determine trip type
  const getTripType = () => {
    return bookingDetails.returnDate ? "Round Trip" : "One Way";
  };

  // Helper function to get baggage information — supports both formats
  const getBaggageInfo = () => {
    const legs: any[] = bookingDetails.flightData?.legs ?? [];
    const outboundLeg = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
    const inboundLeg = legs.find((l: any) => l.direction === "inbound");

    const onwardBaggage =
      outboundLeg?.segments?.[0]?.bookingInfo?.baggage ||
      bookingDetails.flightData?.onwardFlight?.schedules?.[0]?.baggage ||
      "";
    const returnBaggage =
      inboundLeg?.segments?.[0]?.bookingInfo?.baggage ||
      bookingDetails.flightData?.returnFlight?.schedules?.[0]?.baggage ||
      "";

    if (bookingDetails.returnDate && returnBaggage) {
      if (onwardBaggage === returnBaggage) {
        return `${onwardBaggage} (Both directions)`;
      } else {
        return `Outbound: ${onwardBaggage || "N/A"} | Return: ${returnBaggage}`;
      }
    }

    return onwardBaggage || "N/A";
  };

  // Component for displaying individual flight — supports both new legs[] format and old onwardFlight/returnFlight format
  const FlightLegDisplay = ({ leg, title }: { leg: any; title: string }) => {
    if (!leg) return null;

    // New format: leg.segments[]; Old format: flight.schedules[]
    const segments = leg.segments || leg.schedules || [];
    const totalDuration = formatDuration(leg.duration ?? leg.elapsedTime);
    const stops = (leg.segments ? segments.length - 1 : leg.stops) ?? 0;

    return (
      <div className="mb-4 last:mb-0">
        <div className="flex items-center gap-2 mb-2">
          <FaPlane className="text-primary text-sm" />
          <span className="font-medium text-heading text-sm">{title}</span>
        </div>

        {segments.map((segment: any, idx: number) => (
          <div
            key={idx}
            className="bg-white rounded-lg p-3 mb-2 last:mb-0 shadow-sm"
          >
            {/* Flight header */}
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-black text-sm">
                {segment.marketingCarrier?.code ?? segment.flightCode}{" "}
                {segment.marketingCarrier?.flightNumber ?? segment.flightNumber}
              </span>
              <span className="text-xs text-heading">
                {segment.equipment || "N/A"}
              </span>
            </div>

            {/* Route and timing */}
            <div className="grid grid-cols-3 gap-2 items-center text-xs">
              <div className="text-center">
                <div className="font-medium text-black">
                  {segment.departure?.airport?.code ?? segment.departure?.airport ?? "-"}
                </div>
                <div className="text-heading">
                  {formatTime(segment.departure?.dateTime ?? segment.departure?.time)}
                </div>
                {segment.departure?.terminal && (
                  <div className="text-xs text-heading">
                    T{segment.departure.terminal}
                  </div>
                )}
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <div className="h-px bg-gray-300 flex-1"></div>
                  <FaPlane className="text-primary mx-2 text-xs" />
                  <div className="h-px bg-gray-300 flex-1"></div>
                </div>
                <div className="text-xs text-heading">
                  {formatDuration(segment.duration ?? segment.elapsedTime)}
                </div>
              </div>

              <div className="text-center">
                <div className="font-medium text-black">
                  {segment.arrival?.airport?.code ?? segment.arrival?.airport ?? "-"}
                </div>
                <div className="text-heading">
                  {formatTime(segment.arrival?.dateTime ?? segment.arrival?.time)}
                </div>
                {segment.arrival?.terminal && (
                  <div className="text-xs text-heading">
                    T{segment.arrival.terminal}
                  </div>
                )}
                {(segment.arrival?.dateAdjustment ?? 0) > 0 && (
                  <div className="text-xs text-red-500">
                    +{segment.arrival.dateAdjustment}d
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Flight summary */}
        <div className="flex justify-between items-center text-xs text-heading mt-2">
          <span className="flex items-center gap-1">
            <FaClock className="text-primary" />
            Total: {totalDuration}
          </span>
          <span>
            {stops <= 0 ? "Non-stop" : `${stops} stop${stops > 1 ? "s" : ""}`}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center overflow-y-auto p-4 font-sans"
    >
      {/* Sits above this popup: the booking exists by now, and the only way on
          from here is to approve it or walk away leaving it unpaid. */}
      {cashOtp && (
        <CashOtpDialog
          bookingId={cashOtp.bookingId}
          challenge={cashOtp.challenge}
          onAuthorised={() => {
            const id = cashOtp.bookingId;
            setCashOtp(null);
            void completeTicketing(id);
          }}
          onCancel={() => {
            setCashOtp(null);
            setError(
              "This booking has not been paid for and no ticket was issued."
            );
          }}
        />
      )}

      <div
        ref={modalRef}
        className="bg-white rounded-2xl max-w-5xl w-full mx-4 p-0 shadow-2xl relative"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-8 pt-8 pb-4 border-b border-gray-200 rounded-t-2xl bg-primary">
          <h2 className="text-subheading text-white font-sora">
            Review & Confirm Details
          </h2>
          <button
            className="text-white text-3xl font-bold hover:text-gray-200"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* INFO GRID */}
        <div className="grid md:grid-cols-3 gap-6 px-8 py-8 bg-[#F5F8FF] rounded-b-2xl">
          {/* Passenger Summary */}
          <div className="bg-card rounded-xl p-4 h-full flex flex-col shadow">
            <div className="flex items-center gap-2 border-b pb-2 mb-3">
              <FaRegClipboard className="text-primary" />
              <span className="text-subheading font-sora text-heading">
                Passenger Details
              </span>
            </div>
            <div className="overflow-y-auto max-h-[400px] space-y-4 pr-2">
              {passengerData.map((p, idx) => (
                <div
                  key={idx}
                  className="mb-2 pb-2 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0 bg-white rounded-lg shadow-sm p-4"
                >
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">Title</span>
                      <span className="text-black">{p.title}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">Name</span>
                      <span className="text-black">{`${p.firstName} ${p.lastName}`}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">Phone</span>
                      {/* The full international number, not the bare digits the
                          form holds. This is where the e-ticket is sent and what
                          goes into the PNR, and the country code is chosen from
                          a dropdown that defaults to +971 — so a number typed
                          for another country silently became a UAE one, and the
                          only place it could have been caught showed the digits
                          alone. Show what will actually be used. */}
                      <span className="text-black">
                        {withDialCode(p.countryCode, p.phone) || p.phone}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">Email</span>
                      <span className="text-black">{p.email}</span>
                    </div>
                    {p.ffNumber && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-heading">
                          FF Number
                        </span>
                        <span className="text-black">{p.ffNumber}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">
                        Passport #
                      </span>
                      <span className="text-black">{p.passportNumber}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">DOB</span>
                      <span className="text-black">{p.dob}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">Expiry</span>
                      <span className="text-black">{p.expiryDate}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">
                        Issuing Country
                      </span>
                      <span className="text-black">{p.issuingCountry}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-heading">
                        Trip Insurance
                      </span>
                      <span className="text-black">
                        {p.insured ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enhanced Flight Summary */}
          <div className="bg-card rounded-xl p-6 h-full flex flex-col shadow">
            <div className="flex items-center gap-2 border-b pb-2 mb-3">
              <FaRegClipboard className="text-primary" />
              <span className="text-subheading font-sora text-heading">
                Flight Summary
              </span>
            </div>

            <div className="overflow-y-auto max-h-[400px] pr-2">
              {/* Trip Type and Basic Info */}
              <div className="mb-4 p-3 bg-white rounded-lg shadow-sm">
                <div className="grid gap-2 text-sm">
                  <InfoRow label="Trip Type" value={getTripType()} />
                  <InfoRow label="Class" value={getDisplayedClass()} />
                  <InfoRow
                    label="Adults"
                    value={bookingDetails.passengerCounts?.ADT ?? 0}
                  />
                  {(bookingDetails.passengerCounts?.CHD ?? 0) > 0 && (
                    <InfoRow
                      label="Children"
                      value={bookingDetails.passengerCounts?.CHD ?? 0}
                    />
                  )}

                  {(bookingDetails.passengerCounts?.INF ?? 0) > 0 && (
                    <InfoRow
                      label="Infants"
                      value={bookingDetails.passengerCounts?.INF ?? 0}
                    />
                  )}
                </div>
              </div>

              {/* Onward Flight */}
              <FlightLegDisplay
                leg={(() => {
                  const legs = bookingDetails.flightData?.legs ?? [];
                  const outbound = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
                  return outbound ?? bookingDetails.flightData?.onwardFlight;
                })()}
                title="Outbound Flight"
              />

              {/* Return Flight (if exists) */}
              {(bookingDetails.flightData?.legs?.some((l: any) => l.direction === "inbound") ||
                bookingDetails.flightData?.returnFlight) && (
                  <FlightLegDisplay
                    leg={
                      bookingDetails.flightData?.legs?.find((l: any) => l.direction === "inbound") ??
                      bookingDetails.flightData?.returnFlight
                    }
                    title="Return Flight"
                  />
                )}

              {/* Baggage Information */}
              <div className="mt-4 p-3 bg-white rounded-lg shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaSuitcase className="text-primary text-sm" />
                  <span className="font-medium text-heading text-sm">
                    Baggage Allowance
                  </span>
                </div>
                <div className="text-xs text-black p-2">{getBaggageInfo()}</div>
              </div>
            </div>
          </div>

          {/* Fare Summary */}
          <div className="bg-white rounded-xl p-6 flex flex-col justify-between h-full shadow">
            <div>
              <div className="flex items-center gap-2 border-b pb-2 mb-3">
                <FaRegClipboard className="text-primary" />
                <span className="text-subheading font-sora text-heading">
                  Fare Summary
                </span>
              </div>
              <div className="space-y-2 text-black">
                <div className="flex justify-between">
                  <span>Base Fare:</span>
                  <span>
                    {currency} {baseFare}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Taxes:</span>
                  <span>
                    {currency} {taxes}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Service Fee:</span>
                  <span>
                    {currency} {serviceFee}
                  </span>
                </div>
                {seatTotal > 0 && (
                  <div className="flex justify-between">
                    <span>Seats:</span>
                    <span>
                      {currency} {seatTotal.toFixed(2)}
                    </span>
                  </div>
                )}

                {appliedExtraFee > 0 && (
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Additional service fee</span>
                    <span>+ {currency} {appliedExtraFee.toFixed(2)}</span>
                  </div>
                )}
                {appliedDiscount > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Discount:</span>
                    <span>− {currency} {appliedDiscount.toFixed(2)}</span>
                  </div>
                )}

                {/* Only where there is something to give away. A customer, an
                    expired quote and a fare with no markup on it all come back
                    from the server as "not allowed", and all three render as
                    nothing rather than as a disabled box nobody can explain. */}
                {discountCap?.allowed && (
                  <div className="pt-3">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-heading">
                            Discount
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setDiscountInput("");
                              setExtraFeeInput("");
                            }}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            Clear
                          </button>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{currency}</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={maxDiscount}
                            step="0.01"
                            value={discountInput}
                            onChange={(e) => setDiscountInput(e.target.value)}
                            disabled={extraFeeInUse}
                            placeholder="0.00"
                            className="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
                          />
                          <button
                            type="button"
                            onClick={() => setDiscountInput(maxDiscount.toFixed(2))}
                            disabled={extraFeeInUse}
                            className="text-xs text-primary hover:underline disabled:text-gray-300 disabled:no-underline"
                          >
                            Max
                          </button>
                        </div>

                        {/* The other direction. Disabled while a discount is
                            being given: a sale carries one or the other, and a
                            net of the two is a figure nobody reads back
                            cleanly. The server refuses the combination too. */}
                        <div className="border-t border-gray-200 pt-3">
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            Additional service fee
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">{currency}</span>
                            <input
                              type="number"
                              inputMode="decimal"
                              min={0}
                              step="0.01"
                              value={extraFeeInput}
                              onChange={(e) => setExtraFeeInput(e.target.value)}
                              disabled={discountInUse}
                              placeholder="0.00"
                              className="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
                            />
                          </div>
                          {!extraFeeValid && (
                            <p className="mt-1 text-xs text-red-600">
                              Enter the additional fee as an amount.
                            </p>
                          )}
                        </div>

                        {!discountValid && (
                          <p className="text-xs text-red-600">
                            {Number.isFinite(typedDiscount) && typedDiscount > maxDiscount
                              ? `The most that can come off is ${currency} ${maxDiscount.toFixed(2)}.`
                              : "Enter the discount as an amount."}
                          </p>
                        )}
                      </div>
                  </div>
                )}

                <div className="pt-8 flex justify-between font-bold text-base">
                  <span>Total:</span>
                  <span>
                    {currency} {grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <FareRulesDisplay
                flightData={bookingDetails?.flightData}
                passengerCounts={bookingDetails?.passengerCounts}
                departureDate={bookingDetails?.departureDate}
                returnDate={bookingDetails?.returnDate}
                currency={bookingDetails?.flightData?.pricingInfo?.currency ?? bookingDetails?.flightData?.totalFare?.currency ?? "AED"}
                displayMode="popup"
                variant="compact"
              />
            </div>
            <div className="pt-6">
              {/* Where the agent stands against today's limit, shown before
                  they commit rather than when the sale is refused. */}
              {canChoosePayment && (
                <div className="mb-4">
                  <DailyLimitBar />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-heading font-medium mb-2">
                  Payment Method
                </label>
                {!cardEnabled ? (
                  // One option, so nothing to choose. Shown rather than hidden
                  // so it is obvious what is about to happen.
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-700 text-sm">
                    💵 Cash
                  </div>
                ) : canChoosePayment ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("cash")}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition ${paymentMethod === "cash"
                          ? "bg-primary border-primary text-white shadow-sm"
                          : "bg-white border-gray-300 text-gray-700 hover:border-primary"
                        }`}
                    >
                      💵 Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition ${paymentMethod === "card"
                          ? "bg-primary border-primary text-white shadow-sm"
                          : "bg-white border-gray-300 text-gray-700 hover:border-primary"
                        }`}
                    >
                      💳 Credit Card
                    </button>
                  </div>
                ) : (
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-600 text-sm">
                    💳 Credit Card
                  </div>
                )}
              </div>
              <button
                className="w-full bg-primary text-white font-sora text-lg font-semibold py-3 rounded-xl shadow hover:bg-blue-900 transition disabled:opacity-60"
                onClick={async () => {
                  setError(null);
                  setLoading(true);
                  try {
                    if (!user) {
                      setError("You must be logged in to proceed.");
                      return;
                    }
                    // With card switched off, cash is the only route — including
                    // for a customer, who cannot reach this screen in phase 1
                    // anyway because public access is closed.
                    if (!cardEnabled || (paymentMethod === "cash" && canChoosePayment)) {
                      await handleCashPayment();
                    } else {
                      await handleCardPayment();
                    }
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || discountBlocks}
              >
                {loading
                  ? "Processing..."
                  : !cardEnabled || paymentMethod === "cash"
                    ? "Proceed with Cash"
                    : "Proceed to Payment"}
              </button>
              {error && (
                <div className="mt-3 text-red-600 text-sm text-center">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function formatSabreDateTime(date?: string, time?: string) {
  // Ensure date and time are both strings
  const safeDate = typeof date === "string" ? date : "";
  const safeTime = typeof time === "string" ? time : "";
  const cleanTime = safeTime
    ? safeTime.split("+")[0].split("-")[0].slice(0, 5)
    : "00:00";
  return `${safeDate}T${cleanTime}`;
}

// Utility to add days to a date string (YYYY-MM-DD)
function addDays(dateStr: string, days: number) {
  if (!dateStr) return ""; // Guard against undefined
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export default ReviewAndPayPopup;
